/**
 * Splits text into Devanagari and other runs, so an English page can mark
 * each Hindi run lang="hi" (WCAG 3.1.2, language of parts) without wrapping
 * the Latin text around it. A run is one or more Devanagari words, together
 * with the spaces and punctuation between them; leading or trailing spaces and
 * punctuation stay outside it (c-a11y-11).
 */
import { createElement, type ReactNode } from "react";

const DEVANAGARI_RUN = /[ऀ-ॿ]+(?:[\s\p{P}‌‍]+[ऀ-ॿ]+)*/gu;

export type TextRun = { text: string; hindi: boolean };

export function hindiRuns(text: string): TextRun[] {
  const runs: TextRun[] = [];
  let last = 0;
  for (const match of text.matchAll(DEVANAGARI_RUN)) {
    const start = match.index ?? 0;
    if (start > last) runs.push({ text: text.slice(last, start), hindi: false });
    runs.push({ text: match[0], hindi: true });
    last = start + match[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last), hindi: false });
  return runs;
}

/**
 * `text` as JSX children with each Devanagari run in <span lang="hi">, for an
 * English page that renders a string it does not own (engine labels, generated
 * reference rows, FAQ copy shared with JSON-LD). The text content is unchanged.
 */
export function taggedHindi(text: string): ReactNode[] {
  return hindiRuns(text).map((run, index) =>
    run.hindi ? createElement("span", { key: index, lang: "hi" }, run.text) : run.text
  );
}
